/* eslint-disable consistent-return */
import { useCallback, useEffect, useRef, useState } from 'react'

const getScrollableParent = (el: HTMLElement | null): HTMLElement | null => {
  if (!el) return null
  const containerSection = document.querySelector('.container section')
  if (containerSection && containerSection.contains(el)) {
    return containerSection as HTMLElement
  }
  return null
}

export interface DropdownPosition {
  top: number
  left: number
  width: number | string
}

interface UseDropdownOptions {
  open: boolean
  onClose: () => void
  optionsLength: number
  onSelect: (index: number) => void
  initialFocusedIndex?: number
  disableInitialFocus?: boolean
}

const defaultPosition: DropdownPosition = { top: -10000, left: -10000, width: 0 }

export const useDropdown = <T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  optionsLength,
  onSelect,
  initialFocusedIndex,
  disableInitialFocus = false,
}: UseDropdownOptions) => {
  const anchorRef = useRef<T | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const optionRefs = useRef<(HTMLElement | null)[]>([])

  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [menuIsBelowAnchor, setMenuIsBelowAnchor] = useState<boolean | undefined>(undefined)
  const [position, setPosition] = useState<DropdownPosition>(defaultPosition)

  const defaultComputePosition = useCallback((anchor: T, menu: HTMLDivElement) => {
    const rect = anchor.getBoundingClientRect()
    const menuHeight = menu.getBoundingClientRect().height

    if (!menuHeight || menuIsBelowAnchor === undefined) return

    const newTop = !menuIsBelowAnchor
      ? rect.top + window.scrollY - menuHeight - 4
      : rect.bottom + window.scrollY + 4

    setPosition({
      top: newTop,
      left: rect.left + window.scrollX,
      width: rect.width,
    })
  }, [menuIsBelowAnchor])

  const defaultComputeAboveOrBelow = useCallback((anchor: T, menu: HTMLDivElement) => {
    const rect = anchor.getBoundingClientRect()
    const menuHeight = menu.getBoundingClientRect().height

    if (!menuHeight) return

    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const fitsBelow = spaceBelow >= menuHeight || spaceBelow >= spaceAbove
    setMenuIsBelowAnchor(fitsBelow)
  }, [])

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !menuRef.current) return

    defaultComputePosition(anchorRef.current, menuRef.current)
  }, [defaultComputePosition])

  const onOptionKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          const next = index < optionsLength - 1 ? index + 1 : 0
          setFocusedIndex(next)
          optionRefs.current[next]?.focus()
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          const prev = index > 0 ? index - 1 : optionsLength - 1
          setFocusedIndex(prev)
          optionRefs.current[prev]?.focus()
          break
        }
        case 'Tab': {
          e.preventDefault()
          const next = e.shiftKey ? index - 1 : index + 1
          if (next >= 0 && next < optionsLength) {
            setFocusedIndex(next)
            optionRefs.current[next]?.focus()
          } else {
            onClose()
            anchorRef.current?.focus()
          }
          break
        }
        case 'Enter':
          onSelect(index)
          break
        case 'Escape':
          onClose()
          anchorRef.current?.focus()
          break
        default:
          break
      }
    },
    [optionsLength, onClose, onSelect]
  )

  // Register resize and scroll listeners to update position
  useEffect(() => {
    if (!open) {
      setMenuIsBelowAnchor(undefined)
      setPosition(defaultPosition)
      return
    }

    updatePosition()
    const scrollableAncestor = getScrollableParent(anchorRef.current)
    const listeners: [Window | HTMLElement, string, EventListener][] = [
      [window, 'resize', updatePosition],
      [window, 'scroll', updatePosition],
      ...(scrollableAncestor
        ? [[scrollableAncestor, 'scroll', updatePosition] as [HTMLElement, string, EventListener]]
        : []),
    ]

    listeners.forEach(([target, event, handler]) => target.addEventListener(event, handler))
    return () =>
      listeners.forEach(([target, event, handler]) =>
        target.removeEventListener(event, handler)
      )
  }, [open, updatePosition, optionsLength])

  // Handle click outside to close
  useEffect(() => {
    if (!open) return

    // Calculate if menu should be above or below anchor on open
    if (anchorRef.current && menuRef.current) {
      defaultComputeAboveOrBelow(anchorRef.current, menuRef.current)
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !anchorRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onClose, defaultComputeAboveOrBelow])

  // Focus and scroll to initial option when dropdown opens
  useEffect(() => {
    if (!open) return
    if (initialFocusedIndex == null || initialFocusedIndex < 0) return

    // Focus after a tick to ensure menu and options are rendered
    const timeoutId = setTimeout(() => {
      const el = optionRefs.current[initialFocusedIndex]
      if (!el) return

      setFocusedIndex(initialFocusedIndex)

      // Scroll into selection without focusing if disabled
      if (!disableInitialFocus) {
        el.focus()
      }

      setTimeout(() => {
        el.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      }, 0)
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [open, initialFocusedIndex, disableInitialFocus])

  return {
    anchorRef,
    menuRef,
    optionRefs,
    focusedIndex,
    position,
    onOptionKeyDown,
  }
}
